import React, { useState } from 'react';
import { 
  X, 
  Mic, 
  FileText, 
  FileSpreadsheet, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { Inspection, InspectionFrame } from '../../types';
import { InspectionService } from '../../services/inspectionService';
import { ReportPreview } from './ReportPreview';

interface ReportGeneratorProps {
  inspectionId: string;
  inspection: Inspection;
  frames: InspectionFrame[];
  onClose: () => void;
  onComplete: (docUrl: string) => void;
}

type GenerationStep = 'idle' | 'transcribing' | 'generating' | 'creating_doc' | 'complete' | 'error';

interface StepStatus {
  transcribing: 'pending' | 'in_progress' | 'complete' | 'error';
  generating: 'pending' | 'in_progress' | 'complete' | 'error';
  creating_doc: 'pending' | 'in_progress' | 'complete' | 'error';
}

export const ReportGenerator: React.FC<ReportGeneratorProps> = ({
  inspectionId,
  inspection: _inspection,
  frames,
  onClose,
  onComplete
}) => {
  const [currentStep, setCurrentStep] = useState<GenerationStep>('idle');
  const [stepStatus, setStepStatus] = useState<StepStatus>({
    transcribing: 'pending',
    generating: 'pending',
    creating_doc: 'pending'
  });
  const [error, setError] = useState<string | null>(null);
  const [reportMarkdown, setReportMarkdown] = useState<string | null>(null);
  const [reportSummary, setReportSummary] = useState<string | null>(null);
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const [transcriptionProgress, setTranscriptionProgress] = useState({ current: 0, total: 0 });
  const [showPreview, setShowPreview] = useState(false);

  const inspectionService = InspectionService.getInstance();

  // Count frames with audio that need transcription
  const framesWithAudio = frames.filter(f => f.audioUrl && !f.audioTranscript);
  const hasAudioToTranscribe = framesWithAudio.length > 0;

  // Start the generation process
  const startGeneration = async () => {
    setCurrentStep('transcribing');
    setError(null);

    try {
      // Step 1: Transcribe audio (if any)
      if (hasAudioToTranscribe) {
        setStepStatus(prev => ({ ...prev, transcribing: 'in_progress' }));
        setTranscriptionProgress({ current: 0, total: framesWithAudio.length });

        for (let i = 0; i < framesWithAudio.length; i++) {
          const frame = framesWithAudio[i];
          try {
            console.log(`🎙️ Transcribing audio for frame ${frame.id}...`);
            await inspectionService.transcribeAudio(inspectionId, frame.id);
            setTranscriptionProgress({ current: i + 1, total: framesWithAudio.length });
          } catch (err) {
            console.warn(`⚠️ Failed to transcribe frame ${frame.id}:`, err);
            // Continue with other frames even if one fails
          }
        }

        setStepStatus(prev => ({ ...prev, transcribing: 'complete' }));
      } else {
        setStepStatus(prev => ({ ...prev, transcribing: 'complete' }));
      }

      // Step 2: Generate AI Report
      setCurrentStep('generating');
      setStepStatus(prev => ({ ...prev, generating: 'in_progress' }));

      console.log('📝 Generating AI report...');
      const { markdown, summary } = await inspectionService.generateReport(inspectionId);
      setReportMarkdown(markdown);
      setReportSummary(summary);
      setStepStatus(prev => ({ ...prev, generating: 'complete' }));

      // Show preview instead of immediately creating doc
      setShowPreview(true);

    } catch (err: any) {
      console.error('❌ Error in report generation:', err);
      setError(err.message || 'Report generation failed');
      setCurrentStep('error');
      
      // Update step status based on where we failed
      if (stepStatus.transcribing === 'in_progress') {
        setStepStatus(prev => ({ ...prev, transcribing: 'error' }));
      } else if (stepStatus.generating === 'in_progress') {
        setStepStatus(prev => ({ ...prev, generating: 'error' }));
      }
    }
  };

  // Create Google Doc after preview approval
  const createGoogleDoc = async (finalMarkdown: string) => {
    try {
      setShowPreview(false);
      setCurrentStep('creating_doc');
      setStepStatus(prev => ({ ...prev, creating_doc: 'in_progress' }));

      console.log('📄 Creating Google Doc...');
      const url = await inspectionService.createGoogleDoc(inspectionId, finalMarkdown);
      setDocUrl(url);
      setStepStatus(prev => ({ ...prev, creating_doc: 'complete' }));
      setCurrentStep('complete');

      // Update inspection status
      await inspectionService.updateInspection(inspectionId, {
        status: 'completed',
        reportDocUrl: url
      });

    } catch (err: any) {
      console.error('❌ Error creating Google Doc:', err);
      setError(err.message || 'Failed to create Google Doc');
      setCurrentStep('error');
      setStepStatus(prev => ({ ...prev, creating_doc: 'error' }));
    }
  };

  // Get step display info
  const getStepInfo = (step: keyof StepStatus) => {
    const status = stepStatus[step];
    
    const icons = {
      transcribing: <Mic className="w-5 h-5" />,
      generating: <FileText className="w-5 h-5" />,
      creating_doc: <FileSpreadsheet className="w-5 h-5" />
    };

    const labels = {
      transcribing: 'Transcribe Audio',
      generating: 'Generate AI Report',
      creating_doc: 'Create Google Doc'
    };

    const descriptions = {
      transcribing: hasAudioToTranscribe 
        ? `Transcribing ${framesWithAudio.length} audio recording${framesWithAudio.length > 1 ? 's' : ''}` 
        : 'No audio to transcribe',
      generating: 'Analyzing frames and generating inspection report',
      creating_doc: 'Creating document in Drive folder'
    };

    return {
      icon: icons[step],
      label: labels[step],
      description: descriptions[step],
      status
    };
  };

  // Render step indicator
  const renderStep = (step: keyof StepStatus, index: number) => {
    const info = getStepInfo(step);
    
    return (
      <div 
        key={step}
        className={`flex items-start p-4 rounded-lg ${
          info.status === 'in_progress' ? 'bg-orange-50 border border-orange-200' :
          info.status === 'complete' ? 'bg-green-50 border border-green-200' :
          info.status === 'error' ? 'bg-red-50 border border-red-200' :
          'bg-gray-50 border border-gray-200'
        }`}
      >
        {/* Step Number/Status Icon */}
        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
          info.status === 'in_progress' ? 'bg-orange-100 text-orange-600' :
          info.status === 'complete' ? 'bg-green-100 text-green-600' :
          info.status === 'error' ? 'bg-red-100 text-red-600' :
          'bg-gray-200 text-gray-500'
        }`}>
          {info.status === 'in_progress' ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : info.status === 'complete' ? (
            <CheckCircle className="w-5 h-5" />
          ) : info.status === 'error' ? (
            <AlertCircle className="w-5 h-5" />
          ) : (
            <span className="font-semibold">{index + 1}</span>
          )}
        </div>

        {/* Step Content */}
        <div className="flex-1">
          <div className="flex items-center">
            <span className="mr-2 text-gray-500">{info.icon}</span>
            <h4 className={`font-medium ${
              info.status === 'in_progress' ? 'text-orange-900' :
              info.status === 'complete' ? 'text-green-900' :
              info.status === 'error' ? 'text-red-900' :
              'text-gray-900'
            }`}>
              {info.label}
            </h4>
          </div>
          <p className="text-sm text-gray-600 mt-1">{info.description}</p>

          {/* Transcription Progress */}
          {step === 'transcribing' && info.status === 'in_progress' && (
            <div className="mt-2">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span>Progress</span>
                <span>{transcriptionProgress.current} / {transcriptionProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-orange-500 h-2 rounded-full transition-all"
                  style={{ width: `${(transcriptionProgress.current / transcriptionProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Generate Inspection Report</h2>
              <p className="text-sm text-gray-500 mt-1">
                {frames.length} frames • {framesWithAudio.length} with audio
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={currentStep === 'transcribing' || currentStep === 'generating' || currentStep === 'creating_doc'}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Error Display */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center text-red-800">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  <span className="font-medium">Error</span>
                </div>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            )}

            {/* Steps */}
            <div className="space-y-3">
              {renderStep('transcribing', 0)}
              {renderStep('generating', 1)}
              {renderStep('creating_doc', 2)}
            </div>

            {/* Completion State */}
            {currentStep === 'complete' && docUrl && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center text-green-800 mb-2">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span className="font-medium">Report Generated Successfully!</span>
                </div>
                <p className="text-green-700 text-sm mb-3">
                  Your inspection report has been created and saved to Google Drive.
                </p>
                <a
                  href={docUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Report
                </a>
              </div>
            )}

            {/* Summary Preview */}
            {reportSummary && currentStep !== 'complete' && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Report Summary</h4>
                <p className="text-blue-800 text-sm">{reportSummary}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end p-6 border-t border-gray-200 bg-gray-50">
            {currentStep === 'idle' && (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 mr-3"
                >
                  Cancel
                </button>
                <button
                  onClick={startGeneration}
                  disabled={frames.length === 0}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Start Generation
                </button>
              </>
            )}

            {currentStep === 'complete' && (
              <button
                onClick={() => onComplete(docUrl!)}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                Done
              </button>
            )}

            {currentStep === 'error' && (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 mr-3"
                >
                  Close
                </button>
                <button
                  onClick={startGeneration}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  Retry
                </button>
              </>
            )}

            {(currentStep === 'transcribing' || currentStep === 'generating' || currentStep === 'creating_doc') && (
              <button
                disabled
                className="px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed"
              >
                Processing...
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Report Preview Modal */}
      {showPreview && reportMarkdown && (
        <ReportPreview
          markdown={reportMarkdown}
          onConfirm={createGoogleDoc}
          onCancel={() => {
            setShowPreview(false);
            setCurrentStep('idle');
            setStepStatus({
              transcribing: 'pending',
              generating: 'pending',
              creating_doc: 'pending'
            });
          }}
        />
      )}
    </>
  );
};
